import { useEffect } from 'react';

import { HiOutlinePlus, HiOutlineMinus } from 'react-icons/hi';
import {
  HiOutlineChevronDown as CheveronDown,
  HiOutlineChevronUp as CheveronUp,
} from 'react-icons/hi';
import {
  useTable,
  useSortBy,
  useGroupBy,
  useExpanded,
  usePagination,
  useGlobalFilter,
} from 'react-table';

import QtyStepper from 'components/Search/QtyStepper';
import { StyledFlex } from 'components/shared/shared.style.';
import { useApprovals } from 'hooks/useApprovals';

import { checkIfapproval, getOriginalRow } from './Approval/apprvoval.utils';
import GlobalFilter from './Filter/GlobalFilter';
import Pagination from './Pagination/Pagination';
import { EmptyContainer } from './Table.style';
import TableNav from './TableNav/TableNav';
import RenderCell from './TableViews/RenderCell';
import { RenderHeader } from './TableViews/RenderHeader';
import { useA11yNavigation } from './useA11yNavigation';
import { OparationBtn, DetailMobileView } from '../Detail/Detail.style';

type Props = {
  data: Array<any>;
  columns: Array<any>;
  usage: {
    pagination?: boolean;
    filter?: boolean;
    columnsFilter?: boolean;
    enableA11y?: boolean;
  };
  tableProps?: any;
  provideCell?: (row: any) => void;
  actions?: (id?: string, approvalState?: any, originalRow?: any) => React.ReactNode;
  handleOneItem?: any;
  type?: string;
  isTabletDown?: boolean;
  mobileType?: string;
  selectable?: boolean;
  selectedIds?: Record<string, boolean>;
  onToggleRow?: (id: string) => void;
  onToggleAll?: (ids: string[], checked: boolean) => void;
  qtyEnabled?: boolean;
  qtyColumns?: {
    fieldId: string;
    label: string;
    width?: number;
    fieldProperty?: string;
    numOfDecimals?: number;
  }[];
  qtyValues?: Record<string, Record<string, string>>;
  onQtyChange?: (id: string, fieldId: string, value: string) => void;
};

function Table({
  data,
  columns,
  usage,
  tableProps,
  provideCell,
  actions,
  handleOneItem,
  type,
  isTabletDown,
  mobileType,
  selectable = false,
  selectedIds = {},
  onToggleRow,
  onToggleAll,
  qtyEnabled = false,
  qtyColumns = [],
  qtyValues = {},
  onQtyChange,
}: Props) {
  const tableInstance = useTable(
    {
      data,
      columns,
      autoResetPage: false,
      autoResetSortBy: false,
      ...tableProps,
    },
    useGlobalFilter,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination,
  );

  const {
    rows,
    allColumns,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setPageSize,
    setGlobalFilter,
    setSortBy,
    prepareRow,
    getTableProps,
    getTableBodyProps,
    state: { globalFilter, pageIndex, pageSize, sortBy },
  } = tableInstance as any;

  const { tableRef, rowsRefs, activeRowIndex, changeHoverModeIndex } = useA11yNavigation({
    page,
    enabled: usage.enableA11y,
    provideCell: ({ row }: any) => {
      const originalRow = getOriginalRow(row);
      provideCell?.(originalRow);
    },
  });

  const approvalState = useApprovals(allColumns);

  const hasActions = actions && Boolean(actions());
  const tableRows = usage.pagination ? page : rows;

  const visibleRowIds: string[] = selectable
    ? tableRows.map((row: any) => getOriginalRow(row).ID)
    : [];
  const allVisibleSelected =
    selectable &&
    visibleRowIds.length > 0 &&
    visibleRowIds.every((id) => selectedIds[id]);

  if (type === 'Detail' && isTabletDown) {
    useEffect(() => {
      const handleClick = (event: Event) => {
        const target = event.target as HTMLElement;
        if (target.classList.contains('tableEditIcon')) {
          document
            .querySelector('.layoutWrapper')
            ?.scrollTo({ top: 0, behavior: 'smooth' });
        }
      };

      document.addEventListener('click', handleClick);

      return () => {
        document.removeEventListener('click', handleClick);
      };
    }, []);

    return tableRows.map((row: any, rowIndex: number) => {
      prepareRow(row);
      const originalRow = getOriginalRow(row);
      const rowID = originalRow.ID;
      const hasActions = actions && Boolean(actions());

      return (
        <DetailMobileView key={rowID} className='detailMobileView'>
          <div className='detailRowsActions'>
            <span>Actions</span>
            {hasActions &&
              (originalRow.ISGIFT ? (
                <div style={{ opacity: 0.5, pointerEvents: 'none' }}>
                  {actions(rowID)}
                </div>
              ) : (
                actions(rowID)
              ))}
          </div>

          {row.cells.map((cell: any, cellIndex: number) => {
            return (
              <div key={cellIndex} className='detailRows'>
                <p>{cell.column.Header}</p>
                <div>
                  <div
                    style={{
                      display: 'flex',
                      marginRight: '8px',
                      lineHeight: '25px',
                    }}
                  >
                    <div style={{ display: 'flex' }}>
                      {Boolean(
                        row.original?.fields?.counter.filter((each: any) => {
                          return each.FIELDCAPTION === cell.column.Header;
                        }).length,
                      ) && (
                        <>
                          <OparationBtn
                            operation={'subtract'}
                            onClick={
                              originalRow.ISGIFT
                                ? undefined
                                : () => {
                                    handleOneItem(cell.column.Header, rowID, 'subtract');
                                  }
                            }
                            style={
                              originalRow.ISGIFT
                                ? { opacity: 0.5, pointerEvents: 'none' }
                                : {}
                            }
                          >
                            <HiOutlineMinus />
                          </OparationBtn>
                        </>
                      )}
                    </div>
                    {(() => {
                      if (cell.isGrouped) {
                        return (
                          <StyledFlex
                            $gridGap='1rem'
                            $alignItems='center'
                            justifyContent='left'
                          >
                            {(() => {
                              if (row.isExpanded) return <CheveronUp size={21} />;
                              return <CheveronDown size={21} />;
                            })()}
                            {cell.render('Cell')}
                            <p className='badge'>{row.subRows.length}</p>
                          </StyledFlex>
                        );
                      }
                      return (
                        <RenderCell
                          {...{ cell, rowID, approvalState }}
                          isSmall={Boolean(
                            row.original?.fields?.counter.filter((each: any) => {
                              return each.FIELDCAPTION === cell.column.Header;
                            }).length,
                          )}
                        />
                      );
                    })()}
                    <div style={{ display: 'flex' }}>
                      {Boolean(
                        row.original?.fields?.counter.filter((each: any) => {
                          return each.FIELDCAPTION === cell.column.Header;
                        }).length,
                      ) && (
                        <>
                          <OparationBtn
                            operation={'add'}
                            onClick={
                              originalRow.ISGIFT
                                ? undefined
                                : () => {
                                    handleOneItem(cell.column.Header, rowID, 'add');
                                  }
                            }
                            style={
                              originalRow.ISGIFT
                                ? { opacity: 0.5, pointerEvents: 'none' }
                                : {}
                            }
                          >
                            <HiOutlinePlus />
                          </OparationBtn>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </DetailMobileView>
      );
    });
  }

  if (mobileType === 'list' && isTabletDown) {
    return (
      <>
        <GlobalFilter
          enable={Boolean(usage.filter)}
          {...{ globalFilter, setGlobalFilter }}
        />
        <TableNav
          enable={Boolean(usage.columnsFilter)}
          length={rows.length || 0}
          columns={allColumns}
          excelData={data}
          excelColumns={columns}
          approvalState={approvalState}
          tableRows={tableRows}
          setSortBy={setSortBy}
          sortBy={sortBy}
        />
        {tableRows.map((row: any, rowIndex: number) => {
          prepareRow(row);
          const originalRow = getOriginalRow(row);
          const rowID = originalRow.ID;
          const hasActions = actions && Boolean(actions());

          const hasOnlyHeaderFields = row.cells.length <= 2;
          return (
            <DetailMobileView
              key={rowID}
              className={`mobileViewList ${hasOnlyHeaderFields ? 'singleField' : ''}`}
            >
              <div className='detailRowsActions mobileList'>
                {selectable && (
                  <input
                    type='checkbox'
                    aria-label={`select-${rowID}`}
                    checked={Boolean(selectedIds[rowID])}
                    onChange={() => onToggleRow?.(rowID)}
                    style={{ marginRight: '8px' }}
                  />
                )}
                {qtyEnabled &&
                  qtyColumns.map((qtyColumn) => (
                    <div key={qtyColumn.fieldId} style={{ marginRight: '8px' }}>
                      <span style={{ fontSize: '12px', color: 'gray' }}>
                        {qtyColumn.label}
                      </span>
                      <QtyStepper
                        ariaLabel={`qty-${qtyColumn.fieldId}-${rowID}`}
                        value={qtyValues[rowID]?.[qtyColumn.fieldId] ?? ''}
                        width={qtyColumn.width}
                        fieldProperty={qtyColumn.fieldProperty}
                        numOfDecimals={qtyColumn.numOfDecimals}
                        onChange={(val) => onQtyChange?.(rowID, qtyColumn.fieldId, val)}
                      />
                    </div>
                  ))}
                <div className='headerCells'>
                  {row.cells.slice(0, 2).map((cell: any, cellIndex: number) => {
                    const { id, type } = cell.column;
                    return (
                      <div
                        key={cellIndex}
                        className={cellIndex === 0 ? 'headerCellFirst' : ''}
                        onClick={() => {
                          if (selectable) return onToggleRow?.(rowID);
                          if (checkIfapproval(id)) {
                            if (cell.row.isGrouped) {
                              const leafRows = cell.row.leafRows;
                              return approvalState.onMultiChange(leafRows);
                            }
                            return approvalState.onChange(originalRow);
                          }
                          if (provideCell) provideCell(originalRow);
                        }}
                      >
                        <RenderCell
                          {...{ cell, rowID, approvalState, row }}
                          isSmall={Boolean(
                            row.original?.fields?.counter.filter((each: any) => {
                              return each.FIELDCAPTION === cell.column.Header;
                            }).length,
                          )}
                        />
                      </div>
                    );
                  })}
                </div>
                {hasActions &&
                  (originalRow.ISGIFT ? (
                    <div style={{ opacity: 0.5, pointerEvents: 'none' }}>
                      {actions(rowID, approvalState, originalRow)}
                    </div>
                  ) : (
                    actions(rowID, approvalState, originalRow)
                  ))}
              </div>

              {row.cells.slice(2).map((cell: any, cellIndex: number) => {
                const { id, type } = cell.column;
                return (
                  <div
                    key={cellIndex}
                    className='detailRowsList'
                    onClick={() => {
                      if (selectable) return onToggleRow?.(rowID);
                      if (checkIfapproval(id)) {
                        if (cell.row.isGrouped) {
                          const leafRows = cell.row.leafRows;
                          return approvalState.onMultiChange(leafRows);
                        }
                        return approvalState.onChange(originalRow);
                      }
                      if (provideCell) provideCell(originalRow);
                    }}
                  >
                    <p>{cell.column.Header}:</p>
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          marginRight: '8px',
                        }}
                      >
                        <div style={{ display: 'flex' }}>
                          {Boolean(
                            row.original?.fields?.counter.filter((each: any) => {
                              return each.FIELDCAPTION === cell.column.Header;
                            }).length,
                          ) && (
                            <>
                              <OparationBtn
                                operation={'subtract'}
                                onClick={
                                  originalRow.ISGIFT
                                    ? undefined
                                    : () => {
                                        handleOneItem(
                                          cell.column.Header,
                                          rowID,
                                          'subtract',
                                        );
                                      }
                                }
                                style={
                                  originalRow.ISGIFT
                                    ? { opacity: 0.5, pointerEvents: 'none' }
                                    : {}
                                }
                              >
                                <HiOutlineMinus />
                              </OparationBtn>
                            </>
                          )}
                        </div>
                        {(() => {
                          if (cell.isGrouped) {
                            return (
                              <StyledFlex
                                $gridGap='1rem'
                                $alignItems='center'
                                justifyContent='left'
                              >
                                {(() => {
                                  if (row.isExpanded) return <CheveronUp size={21} />;
                                  return <CheveronDown size={21} />;
                                })()}
                                {cell.render('Cell')}
                                <p className='badge'>{row.subRows.length}</p>
                              </StyledFlex>
                            );
                          }
                          return (
                            <RenderCell
                              {...{ cell, rowID, approvalState, row }}
                              isSmall={Boolean(
                                row.original?.fields?.counter.filter((each: any) => {
                                  return each.FIELDCAPTION === cell.column.Header;
                                }).length,
                              )}
                            />
                          );
                        })()}
                        <div style={{ display: 'flex' }}>
                          {Boolean(
                            row.original?.fields?.counter.filter((each: any) => {
                              return each.FIELDCAPTION === cell.column.Header;
                            }).length,
                          ) && (
                            <>
                              <OparationBtn
                                operation={'add'}
                                onClick={
                                  originalRow.ISGIFT
                                    ? undefined
                                    : () => {
                                        handleOneItem(cell.column.Header, rowID, 'add');
                                      }
                                }
                                style={
                                  originalRow.ISGIFT
                                    ? { opacity: 0.5, pointerEvents: 'none' }
                                    : {}
                                }
                              >
                                <HiOutlinePlus />
                              </OparationBtn>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </DetailMobileView>
          );
        })}
      </>
    );
  }

  return (
    <>
      <GlobalFilter
        enable={Boolean(usage.filter)}
        {...{ globalFilter, setGlobalFilter }}
      />
      <TableNav
        enable={Boolean(usage.columnsFilter)}
        length={rows.length || 0}
        columns={allColumns}
        excelData={data}
        excelColumns={columns}
        approvalState={approvalState}
      />
      {(() => {
        if (!tableRows.length) {
          return <EmptyContainer>No data found</EmptyContainer>;
        }
        return (
          <div ref={tableRef} className='container'>
            <table {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup: any, headerIndex: number) => (
                  <tr key={headerIndex} {...headerGroup.getHeaderGroupProps()}>
                    {(() => {
                      if (!selectable) return null;
                      return (
                        <th data-testid='column-select'>
                          <input
                            type='checkbox'
                            aria-label='select-all'
                            checked={allVisibleSelected}
                            onChange={(e) =>
                              onToggleAll?.(visibleRowIds, e.target.checked)
                            }
                          />
                        </th>
                      );
                    })()}
                    {(() => {
                      if (!qtyEnabled) return null;
                      return qtyColumns.map((qtyColumn) => (
                        <th key={qtyColumn.fieldId} data-testid='column-qty'>
                          {qtyColumn.label}
                        </th>
                      ));
                    })()}
                    {(() => {
                      if (!hasActions) return null;
                      return <th data-testid='column-actions'>Actions</th>;
                    })()}
                    {headerGroup.headers.map((column: any, columnIndex: number) => (
                      <th
                        data-cy={`column-${column.Header}`}
                        data-testid='column'
                        key={columnIndex}
                      >
                        <RenderHeader {...{ column, tableRows, approvalState }} />
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {tableRows.map((row: any, rowIndex: number) => {
                  prepareRow(row);
                  const originalRow = getOriginalRow(row);
                  const rowID = originalRow.ID;
                  return (
                    <tr
                      data-cy={`row-${rowID}`}
                      key={rowIndex}
                      {...row.getRowProps()}
                      ref={rowsRefs[rowIndex]}
                      /* eslint-disable react/no-unknown-property */
                      active-row={activeRowIndex === rowIndex ? 'true' : 'false'}
                      onMouseEnter={() => changeHoverModeIndex(rowIndex)}
                    >
                      {(() => {
                        if (!selectable) return null;
                        return (
                          <td
                            data-testid='cell-select'
                            onClick={() => onToggleRow?.(rowID)}
                          >
                            <input
                              type='checkbox'
                              aria-label={`select-${rowID}`}
                              checked={Boolean(selectedIds[rowID])}
                              onChange={() => onToggleRow?.(rowID)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        );
                      })()}
                      {(() => {
                        if (!qtyEnabled) return null;
                        return qtyColumns.map((qtyColumn) => (
                          <td
                            key={qtyColumn.fieldId}
                            data-testid='cell-qty'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <QtyStepper
                              ariaLabel={`qty-${qtyColumn.fieldId}-${rowID}`}
                              value={qtyValues[rowID]?.[qtyColumn.fieldId] ?? ''}
                              width={qtyColumn.width}
                              fieldProperty={qtyColumn.fieldProperty}
                              numOfDecimals={qtyColumn.numOfDecimals}
                              onChange={(val) =>
                                onQtyChange?.(rowID, qtyColumn.fieldId, val)
                              }
                            />
                          </td>
                        ));
                      })()}
                      {(() => {
                        if (!hasActions) return null;
                        // Disable actions for gift lines
                        const isGiftLine = originalRow.ISGIFT;
                        return (
                          <td data-testid='cell-actions'>
                            {isGiftLine ? (
                              <div style={{ opacity: 0.5, pointerEvents: 'none' }}>
                                {actions(rowID)}
                              </div>
                            ) : (
                              actions(rowID)
                            )}
                          </td>
                        );
                      })()}
                      {row.cells.map((cell: any, cellIndex: number) => {
                        const { id, type } = cell.column;
                        return (
                          <td
                            data-cy={`cell-${id}-${cellIndex}`}
                            key={cellIndex}
                            data-testid='cell'
                            is-float={type === 'float' ? 'true' : 'false'}
                            is-grouped={cell.isGrouped ? 'true' : 'false'}
                            is-placeholder={cell.isPlaceholder ? 'true' : 'false'}
                            onClick={() => {
                              if (selectable) return onToggleRow?.(rowID);
                              if (checkIfapproval(id)) {
                                if (cell.row.isGrouped) {
                                  const leafRows = cell.row.leafRows;
                                  return approvalState.onMultiChange(leafRows);
                                }
                                return approvalState.onChange(originalRow);
                              }
                              if (provideCell) provideCell(originalRow);
                            }}
                            {...(() => {
                              if (!cell.isGrouped) return cell.getCellProps();
                              return row.getToggleRowExpandedProps();
                            })()}
                          >
                            <div
                              style={{
                                display: 'flex',
                                marginRight: '8px',
                                lineHeight: '25px',
                              }}
                            >
                              <div style={{ display: 'flex' }}>
                                {Boolean(
                                  row.original?.fields?.counter.filter((each: any) => {
                                    return each.FIELDCAPTION === cell.column.Header;
                                  }).length,
                                ) && (
                                  <>
                                    <OparationBtn
                                      operation={'subtract'}
                                      onClick={
                                        originalRow.ISGIFT
                                          ? undefined
                                          : () => {
                                              handleOneItem(
                                                cell.column.Header,
                                                rowID,
                                                'subtract',
                                              );
                                            }
                                      }
                                      style={
                                        originalRow.ISGIFT
                                          ? { opacity: 0.5, pointerEvents: 'none' }
                                          : {}
                                      }
                                    >
                                      <HiOutlineMinus />
                                    </OparationBtn>
                                  </>
                                )}
                              </div>
                              {(() => {
                                if (cell.isGrouped) {
                                  return (
                                    <StyledFlex
                                      $gridGap='1rem'
                                      $alignItems='center'
                                      justifyContent='left'
                                    >
                                      {(() => {
                                        if (row.isExpanded)
                                          return <CheveronUp size={21} />;
                                        return <CheveronDown size={21} />;
                                      })()}
                                      {cell.render('Cell')}
                                      <p className='badge'>{row.subRows.length}</p>
                                    </StyledFlex>
                                  );
                                }
                                return (
                                  <RenderCell
                                    {...{ cell, rowID, approvalState }}
                                    isSmall={Boolean(
                                      row.original?.fields?.counter.filter(
                                        (each: any) => {
                                          return each.FIELDCAPTION === cell.column.Header;
                                        },
                                      ).length,
                                    )}
                                    width={
                                      row.original?.fields?.editable?.find(
                                        (each: any) =>
                                          each.FIELDCAPTION === cell.column.Header,
                                      )?.FIELDWIDTH || undefined
                                    }
                                  />
                                );
                              })()}
                              <div style={{ display: 'flex' }}>
                                {Boolean(
                                  row.original?.fields?.counter.filter((each: any) => {
                                    return each.FIELDCAPTION === cell.column.Header;
                                  }).length,
                                ) && (
                                  <>
                                    <OparationBtn
                                      operation={'add'}
                                      onClick={
                                        originalRow.ISGIFT
                                          ? undefined
                                          : () => {
                                              handleOneItem(
                                                cell.column.Header,
                                                rowID,
                                                'add',
                                              );
                                            }
                                      }
                                      style={
                                        originalRow.ISGIFT
                                          ? { opacity: 0.5, pointerEvents: 'none' }
                                          : {}
                                      }
                                    >
                                      <HiOutlinePlus />
                                    </OparationBtn>
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}
      <Pagination
        enable={Boolean(usage.pagination)}
        total={rows.length || 0}
        {...{
          nextPage,
          pageSize,
          pageIndex,
          previousPage,
          setPageSize,
          canNextPage,
          canPreviousPage,
        }}
      />
    </>
  );
}

export default Table;
